import React from "react";
import { SetupCard } from "@fas/ui-core";
import {
  FormControl,
  InputAdornment,
  TextField,
  Tooltip,
  Box,
  Grid,
  FormHelperText,
  Switch,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";

import type { TextFieldProps } from "@mui/material";
import type { ErrorsMapper } from "@fas/ui-framework";
import {
  hasError,
  getErrorMessage,
} from "@fas/ui-framework";
import CodeEditor from "../../CodeEditor";
import type { GeneralInfo as GeneralInfoType, Types } from "../types/LinkingInfoForm.types";
import TagsAutocomplete from "../../TagsAutocomplete";

export type Props = GeneralInfoType & {
  onTypeChange: (val: Types) => void;
  onNameChange: (val: string) => void;
  onTagsGenerationChange: (val: Array<string>) => void;
  onTagsDisplayChange: (val: Array<string>) => void;
  onTemplateCodeChange: (val: string) => void;
  onUsePaginationChange: (val: boolean) => void;
  errors: ErrorsMapper;
  tagsGeneration?: Array<string>;
  tagsDisplay?: Array<string>;
  types: Array<Types>;
};

function GeneralInfo({
  type,
  onTypeChange,
  name,
  onNameChange,
  tagsGeneration = [],
  onTagsGenerationChange,
  tagsDisplay = [],
  onTagsDisplayChange,
  templateCode,
  onTemplateCodeChange,
  usePagination,
  onUsePaginationChange,
  errors,
  types,
}: Props) {
  return (
    <SetupCard title="General Info">
      <Grid container columnSpacing={2} width={1}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            fullWidth
            variant="outlined"
            error={hasError("type", errors)}
          >
            <Autocomplete
              id="type"
              options={types}
              value={type}
              size="small"
              getOptionLabel={(x) => x}
              onChange={(_, value) => onTypeChange(value as Types)}
              renderInput={(params): React.ReactElement<TextFieldProps> => (
                <TextField
                  {...params}
                  label="Linking type"
                  variant="outlined"
                  margin="normal"
                  error={hasError("type", errors)}
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": "type",
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Tooltip title="The type of your linking. Notice! You can
                            choose &quot;Linking Type&quot; only once with linking creation.
                            After linking creation, you can't change it."
                        >
                          <HelpOutline />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <FormHelperText>{getErrorMessage("type", errors)}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth error={hasError("name", errors)}>
            <TextField
              label="Name"
              margin="normal"
              variant="outlined"
              value={name}
              size="small"
              error={hasError("name", errors)}
              onChange={({ target: { value } }) => onNameChange(value)}
              inputProps={{
                "data-testid": "name",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="It's the name of your linking and the ID of your linking. Notice!
                      You can choose linking name only once with linking creation. If you change the linking name - your
                      linking on &quot;Template&quot; pages will be broked."
                    >
                      <HelpOutline />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Box ml={2}>
              <FormHelperText>{getErrorMessage("name", errors)}</FormHelperText>
            </Box>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            fullWidth
            variant="outlined"
            error={hasError("tagsGeneration", errors)}
            data-testid="generation-tags"
          >
            <TagsAutocomplete
              multiple
              label="Generation tags"
              tooltipText="Pages with &quot;Generation Tags&quot; will be linked to each other or
                will be displayed on pages with &quot;Display Tags&quot; for &quot;Linking Type&quot;
                &quot;List&quot;."
              value={tagsGeneration}
              onChange={(_event, value) => {
                onTagsGenerationChange(Array.isArray(value) ? value : []);
              }}
              error={hasError("tagsGeneration", errors)}
            />
            <FormHelperText>
              {getErrorMessage("tagsGeneration", errors)}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            fullWidth
            variant="outlined"
            error={hasError("tagsGeneration", errors)}
            data-testid="display-tags"
          >
            <TagsAutocomplete
              multiple
              label="Display Tags"
              value={tagsDisplay}
              tooltipText="This tag needs only for Linking Type &quot;List&quot;. Pages with &quot;Generation Tags&quot;
                will be displayed on pages with &quot;Display Tags&quot;."
              onChange={(_event, value) => {
                onTagsDisplayChange(Array.isArray(value) ? value : []);
              }}
              error={hasError("tagsDisplay", errors)}
              disabled={type === "matrix"}
            />
            <FormHelperText>
              {getErrorMessage("tagsDisplay", errors)}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={12}>
          <FormControl
            fullWidth
            variant="outlined"
            data-testid="html-editor-template-code"
          >
            <FormHelperText>Template code</FormHelperText>

            <CodeEditor
              height="350px"
              language="html"
              value={templateCode}
              onChange={onTemplateCodeChange}
              name="html"
            />
          </FormControl>
        </Grid>

        {type === "list" && (
          <Grid size={12} sx={{ mt: 2 }}>
            <Divider />

            <FormControl
              variant="outlined"
              data-testid="use-pagination-switch"
            >
              <FormControlLabel
                control={(
                  <Switch
                    color="primary"
                    checked={usePagination}
                    onClick={() => onUsePaginationChange(!usePagination)}
                  />
                )}
                label="Use Pagination"
                labelPlacement="start"
              />
            </FormControl>
          </Grid>
        )}
      </Grid>
    </SetupCard>
  );
}

export default React.memo<Props>(GeneralInfo);
